// Feature: features/nma-payer-response-pipeline.md, features/nma-claims-command-center-insights.md, features/nma-payer-data-analysis-jobs.md, features/nma-claims-beta-banner-and-blurred-summaries.md, features/nma-claims-known-issues-tabs.md, features/nma-meta-analysis-report-page.md, features/nma-claims-performance-and-component-extraction.md, features/nma-claims-medical-necessity-tab.md
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Input,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  useToast,
  HStack,
} from '@chakra-ui/react';
import {
  SearchIcon,
  WarningTwoIcon,
  ExternalLinkIcon,
  AddIcon,
} from '@chakra-ui/icons';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import {
  formatCurrency,
  FormattedAnalysisText,
} from './NMAClaims/shared';
import NMAExecutiveSummaryTab from './NMAClaims/NMAExecutiveSummaryTab';
import NMAVisualDiscoveryTab from './NMAClaims/NMAVisualDiscoveryTab';
import NMAClaimsExplorerTab from './NMAClaims/NMAClaimsExplorerTab';
import NMARecordsTrackerTab from './NMAClaims/NMARecordsTrackerTab';
import NMAMedicalNecessityTab from './NMAClaims/NMAMedicalNecessityTab';
import {
  NMAClaimDetailModal,
  NMAAnalysisReportModal,
  NMACustomResearchModal,
} from './NMAClaims/NMAClaimsModals';

const TAB_PARAM_MAP = {
  summary: 0,
  command: 0,
  '0': 0,
  visual: 1,
  discovery: 1,
  '95999': 1,
  baselines: 1,
  '95938': 1,
  '1': 1,
  explorer: 2,
  worklist: 2,
  denials: 2,
  analytics: 2,
  intelligence: 2,
  modifiers: 2,
  pos: 2,
  pos15: 2,
  '2': 2,
  records: 3,
  '3': 3,
  mednec: 4,
  'medical-necessity': 4,
  icd10: 4,
  'pointer-a': 4,
  '4': 4,
  '5': 4,
};

// Self-contained lookup input state so typing a claim number in the header does not re-render tabs
// (see features/nma-claims-performance-and-component-extraction.md)
const QuickClaimLookupInput = React.memo(({ onLookup }) => {
  const [query, setQuery] = useState('');
  return (
    <HStack spacing={1.5}>
      <Input
        placeholder="Lookup Claim # or Case # (e.g. P290750)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onLookup(query);
          }
        }}
        bg="white"
        size="sm"
        w="245px"
      />
      <Button
        size="sm"
        colorScheme="blue"
        leftIcon={<SearchIcon />}
        onClick={() => onLookup(query)}
      >
        Lookup
      </Button>
    </HStack>
  );
});

const NMAClaimsDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Controlled tab index (0: Executive Summary, 1: Visual Stage Discovery, 2: Claims Explorer, 3: Medical Records)
  const [activeTab, setActiveTab] = useState(0);

  // Filter states for Claims Explorer (Tab 3)
  const [search, setSearch] = useState('');
  const [payerFilter, setPayerFilter] = useState('');
  const [claimTypeFilter, setClaimTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [quickFilter, setQuickFilter] = useState('ALL');
  const [cptFilter, setCptFilter] = useState('');
  const [modifierFilter, setModifierFilter] = useState('');

  // Table sort state
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });

  // Claim re-synthesis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Claim detail modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedClaimRow, setSelectedClaimRow] = useState(null);
  const [claimDetailData, setClaimDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Payer Data Analysis jobs & Deep-Dive Report Modal state
  const [isGeneratingJobs, setIsGeneratingJobs] = useState(false);
  const {
    isOpen: isReportModalOpen,
    onOpen: onOpenReportModal,
    onClose: onCloseReportModal,
  } = useDisclosure();
  const [selectedAnalysisItem, setSelectedAnalysisItem] = useState(null);

  // Custom Research Task Modal disclosure (form state lives inside NMACustomResearchModal)
  const {
    isOpen: isCustomTaskOpen,
    onOpen: onOpenCustomTask,
    onClose: onCloseCustomTask,
  } = useDisclosure();

  const fetchDashboard = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await callApi('/api/nma/claims/dashboard', accessToken, { method: 'GET' });
      if (!response.ok) {
        throw new Error('Failed to load NMA claims dashboard data.');
      }
      const json = await response.json();
      setDashboardData(json);
    } catch (err) {
      console.error('Error loading NMA claims dashboard:', err);
      setError(err.message || 'Error loading dashboard.');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search || '');
    const tabKey = (qs.get('tab') || '').trim().toLowerCase();
    if (tabKey && TAB_PARAM_MAP[tabKey] !== undefined) {
      setActiveTab(TAB_PARAM_MAP[tabKey]);
    }
    const qPayer = qs.get('payer');
    const qCpt = qs.get('cpt');
    const qMod = qs.get('modifier');
    const qQuick = qs.get('quick');
    const qSearch = qs.get('search');

    if (qPayer !== null) setPayerFilter(qPayer);
    if (qCpt !== null) {
      setCptFilter(qCpt);
    } else if (tabKey === '95999') {
      setCptFilter('95999');
    } else if (tabKey === 'baselines' || tabKey === '95938') {
      setCptFilter('95938');
    }
    if (qMod !== null) setModifierFilter(qMod);
    if (qQuick !== null) setQuickFilter(qQuick);
    if (qSearch !== null) setSearch(qSearch);
  }, [location.search]);

  useEffect(() => {
    if (accessToken) {
      fetchDashboard();
    }
  }, [accessToken, fetchDashboard]);

  const handleResetFilters = useCallback(() => {
    setSearch('');
    setPayerFilter('');
    setClaimTypeFilter('');
    setStatusFilter('');
    setCategoryFilter('');
    setStartDate('');
    setEndDate('');
    setQuickFilter('ALL');
    setCptFilter('');
    setModifierFilter('');
  }, []);

  const handleOpenClaimDetail = useCallback(async (claimRow) => {
    setSelectedClaimRow(claimRow);
    setClaimDetailData(null);
    setDetailLoading(true);
    onOpen();
    try {
      const res = await callApi(`/nma/payer-response/${encodeURIComponent(claimRow.claim_number)}`, accessToken, {
        method: 'GET',
      });
      if (res.ok) {
        const data = await res.json();
        setClaimDetailData(data);
      }
    } catch (err) {
      console.error('Error loading claim detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, [accessToken, onOpen]);

  const handleQuickClaimLookup = useCallback((rawQuery) => {
    const q = String(rawQuery || '').trim();
    if (!q) return;
    const qLower = q.toLowerCase();
    const allClaims = dashboardData?.claims || [];

    // 1. Check for exact claim_number match (or suffix after dot, e.g. P290750 matching 000105.P290750)
    const exactClaim = allClaims.find((c) => {
      const cn = String(c.claim_number || '').toLowerCase();
      const shortCn = cn.includes('.') ? cn.split('.').pop() : cn;
      return cn === qLower || shortCn === qLower;
    });
    if (exactClaim) {
      handleOpenClaimDetail(exactClaim);
      return;
    }

    // 2. Check for substring matches across claim_number, nma_case_no, case_id, patient_name
    const matches = allClaims.filter((c) => {
      const haystack = `${c.claim_number || ''} ${c.nma_case_no || ''} ${c.case_id || ''} ${c.patient_name || ''}`.toLowerCase();
      return haystack.includes(qLower);
    });

    if (matches.length === 1) {
      handleOpenClaimDetail(matches[0]);
      return;
    }

    if (matches.length > 1) {
      setSearch(q);
      setQuickFilter('ALL');
      setActiveTab(2);
      toast({
        title: `${matches.length} Matching Claims Found`,
        description: `Filtered Claims Explorer to "${q}". Click any claim row to open its timeline.`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'No Matching Claim Found',
      description: `No claim or case matching "${q}" was found in the loaded dataset.`,
      status: 'warning',
      duration: 3500,
      isClosable: true,
    });
  }, [dashboardData?.claims, handleOpenClaimDetail, toast]);

  const handleTriggerAnalyze = useCallback(async (identifierToAnalyze, forceRollup = true) => {
    const target = String(identifierToAnalyze || '').trim();
    if (!target) return;
    setIsAnalyzing(true);
    try {
      const isClaim = /^[PT]\d+/i.test(target.split('.').pop());
      const payload = isClaim
        ? { claim_number: target, options: { force_rollup: forceRollup } }
        : { case_id: target, options: { force_rollup: forceRollup } };

      const res = await callApi('/nma/payer-response/analyze', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        toast({
          title: 'Claim Analysis Complete',
          description: `Analyzed ${target} (Rollup v${data.rollup_version || 1})`,
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
        await fetchDashboard();
        if (selectedClaimRow && (selectedClaimRow.claim_number === target || selectedClaimRow.nma_case_no === target)) {
          handleOpenClaimDetail(selectedClaimRow);
        }
      } else {
        toast({
          title: 'Analysis Failed',
          description: data.message || data.error || 'Could not analyze claim.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAnalyzing(false);
    }
  }, [accessToken, fetchDashboard, handleOpenClaimDetail, selectedClaimRow, toast]);

  const handleGenerateDashboardJobs = useCallback(async (options = {}) => {
    if (!accessToken) return false;
    setIsGeneratingJobs(true);
    try {
      const res = await callApi('/api/nma/claims/dashboard/generate', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        if (data.dashboard) {
          setDashboardData(data.dashboard);
        } else {
          await fetchDashboard();
        }
        toast({
          title: 'Dashboard Cached & Research Jobs Queued',
          description: `${data.queued_jobs_count || 0} payer_data_analysis job(s) queued (${data.skipped_active_job_count || 0} active, ${data.skipped_recent_count || 0} recent skipped).`,
          status: 'success',
          duration: 4500,
          isClosable: true,
        });
        return true;
      }
      throw new Error(data.message || data.error || 'Failed to generate dashboard jobs.');
    } catch (err) {
      toast({
        title: 'Job Generation Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setIsGeneratingJobs(false);
    }
  }, [accessToken, fetchDashboard, toast]);

  const handleOpenAnalysisReport = useCallback((item) => {
    setSelectedAnalysisItem(item);
    onOpenReportModal();
  }, [onOpenReportModal]);

  const handleSubmitCustomTask = useCallback(async (customTaskPayload) => {
    return handleGenerateDashboardJobs({
      custom_task: customTaskPayload,
    });
  }, [handleGenerateDashboardJobs]);

  const handleStatusDrilldown = useCallback((qf) => {
    setQuickFilter(qf || 'ALL');
    setActiveTab(2);
  }, []);

  const requestSort = useCallback((key) => {
    setSortConfig((prev) => {
      const direction = prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc';
      return { key, direction };
    });
  }, []);

  const filteredAndSortedClaims = useMemo(() => {
    const rawClaims = dashboardData?.claims || [];
    const qLower = search.trim().toLowerCase();

    let items = rawClaims.filter((c) => {
      if (quickFilter === 'HIGH_URGENCY' && c.urgency !== 'HIGH') return false;
      if (quickFilter === 'RECORDS_NEEDED' && !(c.records_requested && !c.records_sent)) return false;
      if (
        quickFilter === 'RECORDS_SENT' &&
        !((c.records_sent || c.dispute_submitted || c.open_negotiation_submitted) && c.lifecycle_status !== 'PAID')
      ) {
        return false;
      }
      if (quickFilter === 'PARTIALLY_PAID' && c.lifecycle_status !== 'PARTIALLY_PAID_WITH_LINE_DENIALS') return false;
      if (quickFilter === 'FULL_DENIAL' && !(c.has_active_denial && Number(c.total_paid || 0) === 0)) return false;
      if (quickFilter === 'CH_REJECTED' && c.lifecycle_status !== 'CLEARINGHOUSE_REJECTED') return false;
      if (
        quickFilter === 'PAID' &&
        !(c.lifecycle_status === 'PAID' || c.lifecycle_status === 'ALLOWED_APPLIED_TO_DEDUCTIBLE')
      ) {
        return false;
      }

      if (payerFilter && c.payer_name !== payerFilter) return false;
      if (claimTypeFilter && String(c.claim_type || '').toLowerCase() !== claimTypeFilter.toLowerCase()) return false;
      if (statusFilter && c.lifecycle_status !== statusFilter) return false;
      if (categoryFilter && !(c.denial_categories || []).includes(categoryFilter)) return false;
      if (startDate && c.date_of_service && String(c.date_of_service) < startDate) return false;
      if (endDate && c.date_of_service && String(c.date_of_service) > endDate) return false;

      if (qLower) {
        const haystack = `${c.claim_number || ''} ${c.nma_case_no || ''} ${c.case_id || ''} ${c.patient_name || ''} ${c.payer_name || ''} ${c.lifecycle_status || ''}`.toLowerCase();
        if (!haystack.includes(qLower)) return false;
      }
      return true;
    });

    if (cptFilter && cptFilter !== 'ALL_CLAIMS') {
      items = items.filter((c) => (c.cpts || []).includes(cptFilter));
    }
    if (modifierFilter) {
      items = items.filter((c) => (c.modifiers || []).includes(modifierFilter));
    }

    if (sortConfig?.key) {
      const { key, direction } = sortConfig;
      const numericKeys = ['total_billed', 'total_allowed', 'total_paid', 'total_denied', 'nma_case_no'];
      items = [...items].sort((a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        if (numericKeys.includes(key)) {
          const diff = Number(aVal) - Number(bVal);
          return direction === 'asc' ? diff : -diff;
        }
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
        return direction === 'asc' ? cmp : -cmp;
      });
    }
    return items;
  }, [
    dashboardData?.claims,
    quickFilter,
    search,
    payerFilter,
    claimTypeFilter,
    statusFilter,
    categoryFilter,
    startDate,
    endDate,
    cptFilter,
    modifierFilter,
    sortConfig,
  ]);

  const hasActiveFilters = Boolean(
    search ||
    payerFilter ||
    claimTypeFilter ||
    statusFilter ||
    categoryFilter ||
    startDate ||
    endDate ||
    (cptFilter && cptFilter !== 'ALL_CLAIMS') ||
    modifierFilter ||
    quickFilter !== 'ALL'
  );

  const kpis = dashboardData?.kpis || {};
  const rcmMetrics = dashboardData?.rcm_metrics || {};
  const filterOptions = dashboardData?.filter_options || { payers: [], lifecycle_statuses: [], denial_categories: [] };
  const recordsTracker = dashboardData?.records_tracker || [];
  const customAnalyses = dashboardData?.custom_analyses || [];
  const knownIssues = dashboardData?.known_issues_analytics || {};
  const execSummaryData = knownIssues.executive_summary || {};
  const execKpis = execSummaryData.kpis || {};
  const visualDiscoveryData = knownIssues.visual_discovery || {};
  const medicalNecessityData = knownIssues.medical_necessity || {};

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      {/* Big Red BETA Warning Bar across the top of the page */}
      <Box bg="red.600" color="white" py={2.5} px={6} shadow="md">
        <Flex maxW="1600px" mx="auto" align="center" justify="center" gap={3} wrap="wrap" textAlign="center">
          <Badge bg="white" color="red.700" fontWeight="extrabold" fontSize="xs" px={2.5} py={0.5} borderRadius="sm">
            BETA
          </Badge>
          <HStack spacing={2}>
            <WarningTwoIcon boxSize={4} />
            <Text fontWeight="bold" fontSize="sm" letterSpacing="wide">
              Beta - in development. Values and analysis have not been verified.
            </Text>
          </HStack>
        </Flex>
      </Box>
      <Box maxW="1600px" mx="auto" p={6}>
        {/* Header & Quick Claim Lookup Bar */}
        <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="gray.800">
              NMA Claims &amp; Payer Response Intelligence
            </Heading>
            <Text fontSize="sm" color="gray.600">
              Two-tier clearinghouse (837P / 277CA), EOB/ERA remittance, and Lockbox correspondence analytics
            </Text>
          </Box>
          <HStack spacing={3} wrap="wrap">
            <QuickClaimLookupInput onLookup={handleQuickClaimLookup} />
            <Button
              size="sm"
              colorScheme="purple"
              variant="outline"
              onClick={() => navigate('/nma-appeals')}
            >
              Appeals &amp; Templates
            </Button>
            <Text
              as="span"
              fontSize="sm"
              fontWeight="semibold"
              color="purple.700"
              cursor="pointer"
              textDecoration="underline"
              _hover={{ color: 'purple.900' }}
              onClick={() => navigate('/nma-meta-analysis')}
            >
              Meta-Analysis Report <ExternalLinkIcon mx="2px" />
            </Text>
            <Button
              size="sm"
              colorScheme="teal"
              variant="outline"
              leftIcon={<AddIcon />}
              onClick={onOpenCustomTask}
            >
              New Research Task
            </Button>
          </HStack>
        </Flex>

        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* 6 Industry-Standard Executive KPI Cards (Unblurred Dollars + BETA) */}
        <SimpleGrid columns={{ base: 1, md: 3, lg: 6 }} spacing={3} mb={6}>
          <Card shadow="sm" borderTop="3px solid" borderTopColor="blue.500">
            <CardBody py={3} px={3.5}>
              <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                1. Total Claims Billed (beta)
              </Text>
              <Text fontSize="2xl" fontWeight="extrabold" color="gray.800" my={0.5}>
                {(execKpis.total_claims ?? kpis.total_claims ?? 0).toLocaleString()} Claims
              </Text>
              <Text fontSize="11px" color="gray.600">
                <b>{(execKpis.pro_claims ?? kpis.pro_claims ?? 0).toLocaleString()} Pro</b> /{' '}
                <b>{(execKpis.tech_claims ?? kpis.tech_claims ?? 0).toLocaleString()} Tech</b> ({execKpis.total_cases ?? kpis.total_cases ?? 0} Cases)
              </Text>
              <Text fontSize="11px" color="gray.700" mt={0.5}>
                Billed (beta): <b>{formatCurrency(execKpis.total_billed ?? kpis.total_billed)}</b>
              </Text>
              <Text fontSize="11px" color="gray.600">
                Allowed (beta): <b>{formatCurrency(execKpis.total_allowed ?? kpis.total_allowed)}</b>
              </Text>
            </CardBody>
          </Card>

          <Card shadow="sm" borderTop="3px solid" borderTopColor="green.500">
            <CardBody py={3} px={3.5}>
              <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                2. First-Pass Clean (277CA)
              </Text>
              <Text fontSize="2xl" fontWeight="extrabold" color="green.600" my={0.5}>
                {execKpis.ch_clean_pct ?? rcmMetrics.first_pass_clean_rate ?? 0}% Clean
              </Text>
              <Text fontSize="11px" color="red.600" fontWeight="bold">
                {execKpis.ch_rej_pct ?? 0}% Rejected ({(execKpis.ch_rej_cnt ?? kpis.clearinghouse_rejected_count ?? 0).toLocaleString()} / {(execKpis.total_claims ?? kpis.total_claims ?? 0).toLocaleString()})
              </Text>
              <Text fontSize="11px" color="gray.600" mt={0.5}>
                Passed 277CA: <b>{(execKpis.ch_passed_cnt || 0).toLocaleString()} claims</b>
              </Text>
              <Text fontSize="11px" color="gray.600">
                Blocked Billed (beta): <b>{formatCurrency(execKpis.ch_rej_billed || 0)}</b>
              </Text>
            </CardBody>
          </Card>

          <Card shadow="sm" borderTop="3px solid" borderTopColor="purple.500">
            <CardBody py={3} px={3.5}>
              <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                3. Open Medical Record Holds
              </Text>
              <Text fontSize="2xl" fontWeight="extrabold" color="purple.600" my={0.5}>
                {execKpis.rec_open_pct ?? 0}% Open ({(execKpis.rec_open_cnt ?? kpis.pending_records_count ?? 0).toLocaleString()})
              </Text>
              <Text fontSize="11px" color="gray.700">
                <b>{(execKpis.rec_waiting_cnt ?? kpis.pending_records_count ?? 0).toLocaleString()} Unsent</b> |{' '}
                <b>{(execKpis.rec_sent_cnt ?? kpis.records_sent_count ?? 0).toLocaleString()} Sent (Pending)</b>
              </Text>
              <Text fontSize="11px" color="gray.600" mt={0.5}>
                Ever Held: <b>{execKpis.rec_any_pct ?? 0}%</b> ({(execKpis.rec_any_cnt || 0).toLocaleString()} / {(execKpis.ch_passed_cnt || 0).toLocaleString()})
              </Text>
              <Text fontSize="11px" color="gray.600">
                Held Billed (beta): <b>{formatCurrency(execKpis.rec_open_billed || 0)}</b>
              </Text>
            </CardBody>
          </Card>

          <Card shadow="sm" borderTop="3px solid" borderTopColor="orange.500">
            <CardBody py={3} px={3.5}>
              <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                4. EOB Outcomes ({(execKpis.eob_fin_cnt || 0).toLocaleString()} EOBs)
              </Text>
              <Text fontSize="lg" fontWeight="extrabold" my={1}>
                <Text as="span" color="green.600">{execKpis.paid_full_pct ?? 0}%</Text>
                {' / '}
                <Text as="span" color="orange.600">{execKpis.part_den_pct ?? 0}%</Text>
                {' / '}
                <Text as="span" color="red.600">{execKpis.zero_den_pct ?? 0}%</Text>
              </Text>
              <Text fontSize="11px" color="gray.700">
                <Text as="span" color="green.700" fontWeight="bold">Full: {execKpis.eob_paid_full_cnt || 0}</Text> |{' '}
                <Text as="span" color="orange.700" fontWeight="bold">Part: {execKpis.eob_part_den_cnt || 0}</Text> |{' '}
                <Text as="span" color="red.700" fontWeight="bold">$0: {execKpis.eob_zero_den_cnt || 0}</Text>
              </Text>
              <Text fontSize="11px" color="green.700" mt={0.5}>
                Paid (beta): <b>{formatCurrency(execKpis.total_paid ?? kpis.total_paid)}</b>
              </Text>
              <Text fontSize="11px" color="red.600">
                Denied (beta): <b>{formatCurrency(execKpis.total_denied ?? kpis.total_denied)}</b>
              </Text>
            </CardBody>
          </Card>

          <Card shadow="sm" borderTop="3px solid" borderTopColor="teal.500">
            <CardBody py={3} px={3.5}>
              <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                5. Charge Lag (DOS &rarr; 837P)
              </Text>
              <Text fontSize="2xl" fontWeight="extrabold" color="teal.700" my={0.5}>
                Med {execKpis.med_charge_lag ?? 0} Days
              </Text>
              <Text fontSize="11px" color="gray.600">
                Average: <b>{execKpis.avg_charge_lag ?? 0} days</b>
              </Text>
              <Text fontSize="11px" color="gray.600" mt={0.5}>
                Surgery (DOS) &rarr; 837P Submission
              </Text>
              <Text fontSize="11px" color="gray.700">
                Total DOS &rarr; 1st EOB: <b>Med {execKpis.med_dos_eob ?? 0}d</b>
              </Text>
            </CardBody>
          </Card>

          <Card shadow="sm" borderTop="3px solid" borderTopColor="cyan.500">
            <CardBody py={3} px={3.5}>
              <Text fontSize="11px" fontWeight="bold" color="gray.500" textTransform="uppercase">
                6. Payer Speed (837P &rarr; 1st EOB)
              </Text>
              <Text fontSize="2xl" fontWeight="extrabold" color="cyan.700" my={0.5}>
                Med {execKpis.med_payer_adj ?? 0} Days
              </Text>
              <Text fontSize="11px" color="gray.600">
                Average: <b>{execKpis.avg_payer_adj ?? 0} days</b> at payer
              </Text>
              <Text fontSize="11px" color="gray.700" mt={0.5}>
                In-Flight Now: <b>{(execKpis.awaiting_eob_cnt || 0).toLocaleString()}</b> ({execKpis.awaiting_eob_pct ?? 0}%)
              </Text>
              <Text fontSize="11px" color="gray.600">
                In-Flight Billed (beta): <b>{formatCurrency(execKpis.awaiting_eob_billed || 0)}</b>
              </Text>
            </CardBody>
          </Card>
        </SimpleGrid>

        {loading ? (
          <Flex justify="center" align="center" py={20}>
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : (
          // isLazy ensures only the active tab is mounted in the DOM at any time
          // (see features/nma-claims-performance-and-component-extraction.md)
          <Tabs
            isLazy
            index={activeTab}
            onChange={setActiveTab}
            colorScheme="blue"
            variant="enclosed"
            bg="white"
            borderRadius="lg"
            shadow="sm"
            p={4}
          >
            <TabList flexWrap="wrap">
              <Tab fontWeight="semibold">
                1. Executive Summary (Trends &amp; Aging)
              </Tab>
              <Tab fontWeight="semibold" color="blue.700">
                2. Visual Stage Discovery ({visualDiscoveryData.claims?.length || 0})
              </Tab>
              <Tab fontWeight="semibold">
                3. Claims Explorer &amp; Worklist ({filteredAndSortedClaims.length})
              </Tab>
              <Tab fontWeight="semibold">
                4. Medical Records &amp; Correspondence ({recordsTracker.length})
              </Tab>
              <Tab fontWeight="semibold" color="red.700">
                5. Medical Necessity (EOB) (
                {(medicalNecessityData.denied_lines || []).filter(
                  (l) => l.claim_type === 'pro' && l.cpt_code !== '95999'
                ).length}
                )
              </Tab>
            </TabList>

            <TabPanels>
              {/* TAB 1: EXECUTIVE SUMMARY (STAGE TRENDS & STATUS AGING) */}
              <TabPanel px={0}>
                <NMAExecutiveSummaryTab
                  data={execSummaryData}
                  onStatusDrilldown={handleStatusDrilldown}
                  customAnalyses={customAnalyses}
                  onOpenCustomTask={onOpenCustomTask}
                  onOpenAnalysisReport={handleOpenAnalysisReport}
                  FormattedAnalysisText={FormattedAnalysisText}
                />
              </TabPanel>

              {/* TAB 2: VISUAL STAGE DISCOVERY (BY CPT, PAYER GROUP & TOP 30 CARRIERS) */}
              <TabPanel px={0}>
                <NMAVisualDiscoveryTab data={visualDiscoveryData} initialCpt={cptFilter} />
              </TabPanel>

              {/* TAB 3: CLAIMS EXPLORER & ACTION WORKLIST */}
              <TabPanel px={0}>
                <NMAClaimsExplorerTab
                  filteredAndSortedClaims={filteredAndSortedClaims}
                  totalClaimsCount={dashboardData?.claims?.length || 0}
                  kpis={kpis}
                  filterOptions={filterOptions}
                  search={search}
                  setSearch={setSearch}
                  payerFilter={payerFilter}
                  setPayerFilter={setPayerFilter}
                  claimTypeFilter={claimTypeFilter}
                  setClaimTypeFilter={setClaimTypeFilter}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  cptFilter={cptFilter}
                  setCptFilter={setCptFilter}
                  modifierFilter={modifierFilter}
                  setModifierFilter={setModifierFilter}
                  quickFilter={quickFilter}
                  setQuickFilter={setQuickFilter}
                  hasActiveFilters={hasActiveFilters}
                  onResetFilters={handleResetFilters}
                  sortConfig={sortConfig}
                  onRequestSort={requestSort}
                  onOpenClaimDetail={handleOpenClaimDetail}
                />
              </TabPanel>

              {/* TAB 4: MEDICAL RECORDS & CORRESPONDENCE TRACKER */}
              <TabPanel px={0}>
                <NMARecordsTrackerTab
                  recordsTracker={recordsTracker}
                  onOpenClaimDetail={handleOpenClaimDetail}
                />
              </TabPanel>

              {/* TAB 5: EOB LINE-LEVEL MEDICAL NECESSITY INTELLIGENCE */}
              <TabPanel px={0}>
                <NMAMedicalNecessityTab
                  data={medicalNecessityData}
                  onOpenClaimByNumber={handleQuickClaimLookup}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}

        {/* CLAIM LIFECYCLE & LINE-ITEM DRILL-DOWN MODAL */}
        <NMAClaimDetailModal
          isOpen={isOpen}
          onClose={onClose}
          selectedClaimRow={selectedClaimRow}
          claimDetailData={claimDetailData}
          detailLoading={detailLoading}
          isAnalyzing={isAnalyzing}
          onTriggerAnalyze={handleTriggerAnalyze}
        />

        {/* PAYER DATA ANALYSIS DEEP-DIVE REPORT MODAL */}
        <NMAAnalysisReportModal
          isOpen={isReportModalOpen}
          onClose={onCloseReportModal}
          selectedAnalysisItem={selectedAnalysisItem}
          isGeneratingJobs={isGeneratingJobs}
          onReRunAnalysisJob={handleGenerateDashboardJobs}
        />

        {/* NEW CUSTOM RESEARCH TASK MODAL */}
        <NMACustomResearchModal
          isOpen={isCustomTaskOpen}
          onClose={onCloseCustomTask}
          payers={filterOptions.payers || []}
          isGeneratingJobs={isGeneratingJobs}
          onSubmitCustomTask={handleSubmitCustomTask}
        />
      </Box>
    </Box>
  );
};

export default NMAClaimsDashboard;
